import { FC, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  ButtonOutline,
  ButtonOutlineDanger,
  ButtonOutlinePrimary,
  ButtonPrimary,
  Icon
} from '@lambdacurry/component-library';
import {
  useCurrentCartQuery,
  useMyBoxSettingsQuery,
  useOrderedMealProductsQuery,
  useSaveNeverPreferenceMutation
} from 'lib/home/generated/queries';
import { MedusaLineItem, MedusaProductVariant, OrderedMealProductsQuery } from 'lib/home/generated/types/api';
import { Grid } from 'lib/components';
import { dishContainsAllergens } from 'lib/home/meals/helpers';
import { selectAlternativeMealProductsByPlanSize, setProductVariantOnVariantList } from '../../util/helpers';
import { applyMealsFilters, mealsFilterOptionGroups } from './MealSwap.helpers';
import { Empty, Filters, Loading, MealCard, useFilters } from '..';
import { ConfirmModal } from '../Modals';
import classNames from 'classnames';

export interface MealSwapProps {
  isLoading?: boolean;
  isSuccess?: boolean;
  item: MedusaLineItem;
  onSwap: (cartItemId: string, meal: MedusaProductVariant, handleSuccess?: () => void) => void;
  onBackClick?: () => void;
}

export const MealSwap: FC<MealSwapProps> = ({ isLoading, isSuccess, item: cartItem, onSwap, onBackClick }) => {
  const isMutating = isLoading || isSuccess;
  const [selectedMeal, setSelectedMeal] = useState<string>();
  const queryClient = useQueryClient();
  const { state: filtersState, actions: filterActions } = useFilters();
  const { data: myBoxSettings } = useMyBoxSettingsQuery();
  const { data: mealProducts } = useOrderedMealProductsQuery({ filterPremiums: false });
  const { data: currentCart } = useCurrentCartQuery();
  const cartItems = currentCart?.me?.subscription?.current_order?.medusa_cart.items || [];

  const handleProductVariantUpdate = (variant: MedusaProductVariant) => {
    queryClient.setQueryData(['OrderedMealProducts', { filterPremiums: false }], (query: OrderedMealProductsQuery) => {
      query.standardMeals = setProductVariantOnVariantList(query.standardMeals, variant);
      return query;
    });
  };

  const handleSuccess = () => queryClient.refetchQueries('OrderedMealProducts');
  const handleSwap = (meal: MedusaProductVariant) => {
    if (mealIsInCart(meal)) {
      setAlreadyInCartModal({ isOpen: true, meal });
      return;
    }

    setSelectedMeal(meal.id);
    onSwap(cartItem.id, meal, handleSuccess);
  };

  const { mutate: saveNeverPreference } = useSaveNeverPreferenceMutation({
    onSuccess: ({ saveNeverPreference }) => {
      const itemIndex = mealProducts.standardMeals.findIndex(
        meal => meal.id === saveNeverPreference.product_variant_id
      );

      queryClient.setQueryData(
        ['OrderedMealProducts', { filterPremiums: false }],
        (query: OrderedMealProductsQuery) => {
          query.standardMeals[itemIndex].variant_preference = saveNeverPreference;
          return query;
        }
      );

      setNeverModal({ isOpen: false, meal: null });
    }
  });

  const [alreadyInCartModal, setAlreadyInCartModal] = useState<{ isOpen: boolean; meal: MedusaProductVariant | null }>({
    isOpen: false,
    meal: null
  });

  const [neverModal, setNeverModal] = useState<{ isOpen: boolean; meal: MedusaProductVariant | null }>({
    isOpen: false,
    meal: null
  });

  const [confirmAllergenModal, setConfirmAllergenModal] = useState<{
    isOpen: boolean;
    meal: MedusaProductVariant | null;
  }>({
    isOpen: false,
    meal: null
  });

  if (!mealProducts || !myBoxSettings) return <Loading />;

  const meals = applyMealsFilters(
    selectAlternativeMealProductsByPlanSize(cartItem, mealProducts, myBoxSettings),
    filtersState.filters
  );

  const availableMeals = meals.filter(meal => !meal.variant_preference?.never);
  const neverMeals = meals.filter(meal => meal.variant_preference?.never);
  const { meal_preferences } = myBoxSettings.me;

  const mealIsInCart = (meal: MedusaProductVariant) => {
    const cartItemIds = myBoxSettings?.me?.subscription?.current_order?.medusa_cart?.items.map(item => item.variant_id);

    return cartItemIds.includes(meal.id);
  };

  return (
    <div className="meal-swap">
      <div>
        <h2 className="m-0">Swap Meal</h2>
        <p className="mb-2 text-sm">{cartItem.title}</p>
      </div>

      <Filters className="!pt-0" optionGroups={mealsFilterOptionGroups} />

      {availableMeals.length > 0 && (
        <Grid className="auto-rows-fr">
          {availableMeals.map((meal, index) => {
            const { dietary_information } = meal;
            const alert = dishContainsAllergens(dietary_information, meal_preferences.allergens);
            const isSelectedMeal = selectedMeal === meal.id;
            const isInCart = !!cartItems.find(item => item.variant_id === meal.id);

            return (
              <MealCard
                actions={
                  <>
                    <ButtonPrimary
                      className={classNames('md:order-2', { 'pointer-events-none': isMutating && !isSelectedMeal })}
                      data-cy={`swap-btn-${index}`}
                      disabled={isMutating && isSelectedMeal}
                      icon={<Icon name="swap" />}
                      onClick={() => (alert ? setConfirmAllergenModal({ meal, isOpen: true }) : handleSwap(meal))}
                    >
                      {isMutating && isSelectedMeal ? 'Swapping...' : 'Select'}
                    </ButtonPrimary>

                    <ButtonOutlineDanger
                      className={classNames('md:order-1', { 'pointer-events-none': isMutating && !isSelectedMeal })}
                      data-cy={`never-btn-${index}`}
                      disabled={(isMutating && isSelectedMeal) || isInCart}
                      icon={<Icon name="never" />}
                      onClick={() => setNeverModal({ meal, isOpen: true })}
                    >
                      Never
                    </ButtonOutlineDanger>
                  </>
                }
                alert={alert}
                className="swap-meal-card col-span-12 md:col-span-6 lg:col-span-4"
                id={`meal-card-${meal.id}`}
                key={meal.id}
                meal={meal}
                onProductVariantUpdate={handleProductVariantUpdate}
                tags={isInCart && ['In Cart']}
              />
            );
          })}
        </Grid>
      )}

      {(!meals || meals.length < 1) && (
        <Empty
          title="There are no meals to swap!"
          description="You may need to adjust your search query and filters to see some meals."
          actions={<ButtonOutline onClick={() => filterActions.clearFilters()}>Clear Filters</ButtonOutline>}
        />
      )}

      {neverMeals.length > 0 && (
        <>
          <h2 className="mt-20">Never List</h2>
          <p>These dishes will never appear inside your box.</p>

          <Grid className="auto-rows-fr mb-24">
            {neverMeals.map((meal, index) => (
              <MealCard
                actions={
                  <ButtonOutlinePrimary
                    className={classNames({ 'pointer-events-none': isMutating })}
                    data-cy={`never-removal-btn-${index}`}
                    onClick={() =>
                      saveNeverPreference({
                        saveNeverPreference: { product_variant_id: meal.id, never: false }
                      })
                    }
                  >
                    Add Back
                  </ButtonOutlinePrimary>
                }
                className="col-span-12 sm:col-span-6 md:col-span-4"
                key={meal.id}
                meal={meal}
              />
            ))}
          </Grid>
        </>
      )}

      <ConfirmModal
        isOpen={neverModal.isOpen}
        onAfterClose={() => setNeverModal({ meal: null, isOpen: false })}
        onCancel={() => setNeverModal({ meal: null, isOpen: false })}
        onConfirm={() =>
          saveNeverPreference({ saveNeverPreference: { product_variant_id: neverModal.meal.id, never: true } })
        }
        text="Are you sure you wish place this item in the never category? You will no longer see it on the regular
        selections"
        title={neverModal?.meal?.title}
      />

      <ConfirmModal
        isOpen={alreadyInCartModal.isOpen}
        onAfterClose={() => setAlreadyInCartModal({ isOpen: false, meal: null })}
        onCancel={() => setAlreadyInCartModal({ isOpen: false, meal: null })}
        onConfirm={() => onSwap(cartItem.id, alreadyInCartModal.meal, handleSuccess)}
        text="This item is already in your box. Are you sure you'd like to add it again?"
        title="Just Checking..."
      />

      <ConfirmModal
        isOpen={confirmAllergenModal.isOpen}
        onAfterClose={() => setConfirmAllergenModal({ isOpen: false, meal: null })}
        onCancel={() => setConfirmAllergenModal({ isOpen: false, meal: null })}
        onConfirm={() => {
          handleSwap(confirmAllergenModal.meal);
          setConfirmAllergenModal({ isOpen: false, meal: null });
        }}
        text="Please confirm that you would like this meal added to your box."
        title="This meal has one of your listed allergens"
      />
    </div>
  );
};
